import { useState, useCallback } from 'react';
import { LoginData } from '../types';
import { useAuth } from './useAuth';
import API_ROUTES from './apiRoutes';

interface UseLoginReturn {
  login: (data: LoginData) => void;
  success: boolean;
  loading: boolean;
  error: string | null;
}

export const useLogin = (): UseLoginReturn => {
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { login: authLogin } = useAuth();

  const login = useCallback(async (data: LoginData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_ROUTES.AUTH.login(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.senha,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Tratamento específico para erro de refresh token duplicado
        if (response.status === 500 && errorData.message?.includes('P2002')) {
          throw new Error('Erro interno do servidor. Tente novamente em alguns segundos.');
        }
        
        throw new Error(errorData.message || 'Email ou senha incorretos');
      }

      const result = await response.json();
      
      // Usar o hook de autenticação para salvar os tokens
      authLogin(result.access_token, result.refresh_token);
      
      // Salvar email no localStorage para usar no perfil
      localStorage.setItem('userEmail', data.email);
      
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [authLogin]);

  return { login, success, loading, error };
};
